import { useEffect, useState } from "react";
import { LineChart as LucideChart } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { api } from "../lib/api";

const COLORS = ["#6366F1", "#A855F7", "#3B82F6", "#06B6D4", "#EC4899", "#F59E0B"];
const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 };
const tick = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };

const Panel = ({ title, sub, children, testId, className = "" }) => (
  <div className={`rounded-3xl border border-border bg-card p-6 ${className}`} data-testid={testId}>
    <h3 className="font-semibold tracking-tight">{title}</h3>
    <p className="text-xs text-muted-foreground mb-4">{sub}</p>
    <div className="h-64">{children}</div>
  </div>
);

export default function Insights() {
  const [options, setOptions] = useState([]);
  const [sel, setSel] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/meta/options").then((r) => { setOptions(r.data.options); setSel(r.data.options[0]); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sel) return;
    api.get("/analysis/stats", { params: { exam_board: sel.exam_board, subject: sel.subject } })
      .then((r) => setStats(r.data.stats)).catch(() => setStats(null));
  }, [sel]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="insights-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Analytics</p>
      <h1 className="text-4xl font-bold tracking-tighter">Historical Exam Insights</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">Interactive breakdown of every question asked across past papers.</p>

      <div className="mt-8">
        <Select value={sel ? `${sel.exam_board}|${sel.subject}` : ""} onValueChange={(v) => { const [b, s] = v.split("|"); setSel(options.find((o) => o.exam_board === b && o.subject === s)); }}>
          <SelectTrigger className="w-72" data-testid="insights-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={`${o.exam_board}|${o.subject}`} value={`${o.exam_board}|${o.subject}`}>{o.exam_board} {o.qualification} {o.subject}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel title="Topic Frequency" sub={`Across ${stats.years[0]}–${stats.years[stats.years.length - 1]} (${stats.total_questions} questions analysed)`} testId="chart-topic-frequency">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.topic_frequency} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" allowDecimals={false} tick={tick} />
                <YAxis type="category" dataKey="topic" width={150} tick={tick} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {stats.topic_frequency.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Command Word Trends" sub="How often each command word appears" testId="chart-command-words">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.command_word_frequency}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="command_word" tick={tick} angle={-30} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={tick} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#A855F7" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Assessment Objective Distribution" sub="Share of questions by AO" testId="chart-ao">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.assessment_objective_frequency} dataKey="count" nameKey="ao" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {stats.assessment_objective_frequency.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Question Type Frequency" sub="Mark-band structure of past papers" testId="chart-question-types">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.question_type_frequency} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" allowDecimals={false} tick={tick} />
                <YAxis type="category" dataKey="type" width={170} tick={tick} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#3B82F6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}
      {!stats && (
        <div className="mt-10 rounded-3xl border border-dashed border-border p-16 text-center">
          <LucideChart className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Loading analysis…</p>
        </div>
      )}
    </div>
  );
}
